import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  AppCard,
  AppInput,
  AppTextArea,
  AppFileUploader,
  AppCheckbox,
  AppModal,
  AppNotificationAlert,
  AppRemixIcon,
} from '@/layout/AppComponents';
import { Formik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import ReactDOMServer from 'react-dom/server';
import Translator from '../../../../../components/helpers/Translator';
import { copyToClibpoard } from '../../../../../../minions/string';
import colors from '@/constants/colors';

export default function CommunicationPipelineUserInquiryAddDefaultTemplate(
  props
) {
  const { reportHex, onAdd, onFinish } = props;
  const conditionsFile = useSelector(
    (state) => state.currentPage?.moduleData?.conditionsFile
  );
  const [isCopied, setIsCopied] = useState();
  const [validationSchema] = useState(
    Yup.object().shape({
      nameSurname: Yup.string().required(
        ReactDOMServer.renderToString(<Translator file="module-communicationPipeline" name="required" />)
      ),
      email: Yup.string()
        .required(
          ReactDOMServer.renderToString(<Translator file="module-communicationPipeline" name="required" />)
        )
        .email(
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.enter-valid-email-address"
            />
          )
        ),
      name: Yup.string().required(
        ReactDOMServer.renderToString(<Translator file="module-communicationPipeline" name="required" />)
      ),
      message: Yup.string().required(
        ReactDOMServer.renderToString(<Translator file="module-communicationPipeline" name="required" />)
      ),
      password: Yup.string().required(
        ReactDOMServer.renderToString(<Translator file="module-communicationPipeline" name="required" />)
      ),
      acceptTerms: Yup.bool().oneOf(
        [!!conditionsFile],
        ReactDOMServer.renderToString(<Translator file="module-communicationPipeline" name="required" />)
      ),
    })
  );

  const handleCopyClipboard = useCallback(() => {
    copyToClibpoard(reportHex);
    AppNotificationAlert({
      title: ReactDOMServer.renderToString(
        <Translator
          file="module-communicationPipeline"
          name="accept-terms-conditions"
        />
      ),
      message: ReactDOMServer.renderToString(
        <Translator
          file="module-communicationPipeline"
          name="query-code-copied-clipboard"
        />
      ),
      type: 'success',
      duration: 5000,
      position: 'top-right',
    });
    setIsCopied(true);
  }, [reportHex]);

  return (
    <Container>
      <h5 className="mb-3">
        <Translator file="module-communicationPipeline" name="new-query" />
      </h5>
      <AppCard bodyClassName="minHeight600">
        <Formik
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={validationSchema}
          initialValues={{
            identification: 1,
            nameSurname: '',
            email: '',
            phone: '',
            name: '',
            message: '',
            files: [],
            password: '',
            acceptTerms: false,
          }}
          onSubmit={onAdd}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <h3 className="mt-0">
                <Translator
                  file="module-communicationPipeline"
                  name="first-name-last-name"
                />
                <span className="text-danger">*</span>
              </h3>
              <AppInput
                onChange={(v) => setFieldValue('nameSurname', v)}
                value={values.nameSurname}
                error={errors.nameSurname}
              />
              <h3>
                <Translator file="module-communicationPipeline" name="email" />
                <span className="text-danger">*</span>
              </h3>
              <AppInput
                onChange={(v) => setFieldValue('email', v.trim())}
                value={values.email}
                error={errors.email}
              />
              <h3>
                <Translator file="module-communicationPipeline" name="phone" />
              </h3>
              <AppInput
                onChange={(v) => setFieldValue('phone', v.trim())}
                value={values.phone}
                error={errors.phone}
              />
              <h3 className="mt-0">
                <Translator
                  file="module-communicationPipeline"
                  name="query-title"
                />
                <span className="text-danger">*</span>
              </h3>
              <AppInput
                onChange={(v) => setFieldValue('name', v)}
                value={values.name}
                error={errors.name}
              />
              <h3>
                <Translator
                  file="module-communicationPipeline"
                  name="message"
                />
                <span className="text-danger">*</span>
              </h3>
              <AppTextArea
                onChange={(v) => setFieldValue('message', v)}
                value={values.message}
                error={errors.message}
                resize="vertical"
                height="180px"
              />
              <h3>
                <Translator
                  file="module-communicationPipeline"
                  name="document-file-supports-complements-query-upload-below"
                />
              </h3>
              {values.files.map((file, index) => (
                <div className="mt-2" key={index}>
                  <label
                    className="mb-2 pointer font-weight-light"
                    onClick={() =>
                      setFieldValue(
                        'files',
                        values?.files.filter((f, i) => i !== index)
                      )
                    }
                  >
                    <AppRemixIcon icon="delete-bin-6" color={colors.danger} />{' '}
                    {file?.name}
                  </label>
                </div>
              ))}
              <AppFileUploader
                onChange={(v) =>
                  setFieldValue('files', [...values.files, v?.[0]])
                }
                placeholder={<Translator file="general" name="select.a.file" />}
              />
              <h3>
                <Translator
                  file="module-communicationPipeline"
                  name="enter-password-tracking-your-query"
                />
                <span className="text-danger">*</span>
              </h3>
              <AppInput
                onChange={(v) => setFieldValue('password', v.trim())}
                value={values.password}
                error={errors.password}
                isPassword
              />
              {conditionsFile && (
                <span className="d-inline-block">
                  <AppCheckbox
                    onChange={(v) => setFieldValue('acceptTerms', v)}
                    value={values.acceptTerms}
                    error={errors.acceptTerms}
                    required
                  />
                </span>
              )}
              {conditionsFile && (
                <span className={errors.acceptTerms ? 'text-danger' : ''}>
                  <Translator
                    file="module-communicationPipeline"
                    name="have-read-accept"
                  />{' '}
                  <a
                    href={conditionsFile?.url}
                    target="_blank"
                    className={`text-decoration-underline ${
                      errors.acceptTerms ? 'text-danger' : ''
                    }`}
                  >
                    <Translator
                      file="module-communicationPipeline"
                      name="terms-use-privacy-notice"
                    />
                  </a>
                  .
                </span>
              )}
              <div className="alert alert-warning mt-1">
                <Translator
                  file="module-communicationPipeline"
                  name="upon-completing-submission-window-not-appear-saved-correctly"
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary d-block"
                disabled={isSubmitting}
              >
                <Translator
                  file="module-communicationPipeline"
                  name="send-to"
                />
              </button>
            </form>
          )}
        </Formik>
      </AppCard>
      <AppModal
        show={!!reportHex}
        title={ReactDOMServer.renderToString(
          <Translator file="module-communicationPipeline" name="query-code" />
        )}
        isCloseButton={false}
      >
        <ReportCodeContainer>
          <h3 className="mb-3 pointer" onClick={handleCopyClipboard}>
            <AppRemixIcon icon="file-copy" size={20} />
            {reportHex}
          </h3>
          <h6>
            <Translator
              file="module-communicationPipeline"
              name="this-your-query-code"
            />
          </h6>
          <h6>
            <Translator
              file="module-communicationPipeline"
              name="copy-to-access-your-query"
            />
            .
          </h6>
          {isCopied && (
            <span
              className="btn btn-success btn-sm btn-block"
              onClick={onFinish}
            >
              <Translator file="module-communicationPipeline" name="complete" />
            </span>
          )}
        </ReportCodeContainer>
      </AppModal>
    </Container>
  );
}

const Container = styled.div`
  padding-left: 0.5rem !important;
  padding-bottom: 100px;

  h3 {
    font-size: 14px;
    font-weight: 700;
    margin: 32px 0 0 0;
  }
  .minHeight600 {
    min-height: 600px;
  }
  .user-info {
    border: 1px solid lightgray;
    padding: 24px;
  }
`;

const ReportCodeContainer = styled.div`
  text-align: center;
  h3 {
    font-size: 24px;
  }
`;
